import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { S, REWARD_POOL, pickRelationIcon, pickRelationColor, ageFromDob, buildAffiliateUrl } from "./ibloom/styles";
import { AppTourOverlay, StoryModal, WhyModal, BrowserModal, BookingModal, ReceiptModal, AddMemberForm, HealthProfileModal } from "./ibloom/Modals";
import BuyNowModal from "./ibloom/BuyNowModal";
import CartModal from "./ibloom/CartModal";
import OrderTrackerModal from "./ibloom/OrderTrackerModal";
import DeliveryPartner from "./ibloom/DeliveryPartner";
import EssentialsCheckBookModal from "./ibloom/EssentialsCheckBookModal";
import { ESSENTIALS_GOAL } from "./ibloom/EssentialsCheckBar";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import useIsMobile from "@/lib/useIsMobile";
import { useCart } from "@/context/CartContext";

// Heavy tab screens are code-split so the first paint stays light on mobile.
const MapBazzarTab = lazy(() => import("./ibloom/MapBazzarTab"));
const HealthTab = lazy(() => import("./ibloom/HealthTab"));
const ProfileTab = lazy(() => import("./ibloom/ProfileTab"));

export default function IBloomApp() {
  const { user, loading: authLoading, logout } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [tab, setTab] = useState("shop");
  const [shopView, setShopView] = useState("search");
  const [healthView, setHealthView] = useState("chat");

  const [credits, setCredits] = useState(0);
  const [cash, setCash] = useState(0);
  const [txns, setTxns] = useState([]);
  const [toast, setToast] = useState("");

  const [rawMembers, setRawMembers] = useState([]);
  const [selMember, setSelMember] = useState(null);
  const [chats, setChats] = useState({}); // { memberId: [{id,role,content,urgent}] }
  const [chatSessionIds, setChatSessionIds] = useState({}); // per-member session_id
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [symptoms, setSymptoms] = useState({});

  const [packages, setPackages] = useState([]);
  const [shops, setShops] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  const [whyModal, setWhyModal] = useState(false);
  const [storyModal, setStoryModal] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [receiptModal, setReceiptModal] = useState(false);
  const [bookingModal, setBookingModal] = useState(null);
  const [browserModal, setBrowserModal] = useState(null);
  const [addMemberModal, setAddMemberModal] = useState(false);
  const [profileEditorFor, setProfileEditorFor] = useState(null); // member object or null
  const [bookingsCount, setBookingsCount] = useState(0);
  const [signInModal, setSignInModal] = useState(null); // reason string or null

  // Quick-commerce Buy Now & live order tracker
  const [buyNowProduct, setBuyNowProduct] = useState(null);
  const [trackerOrderId, setTrackerOrderId] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [essentialsBookOpen, setEssentialsBookOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const cart = useCart();

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  // Gate: any action requiring auth routes through this
  const requireAuth = (reason) => {
    if (user) return true;
    setSignInModal(reason || "To continue, please sign in.");
    return false;
  };

  // Enrich members for display
  const members = useMemo(() => rawMembers.map((m) => ({
    ...m,
    age: ageFromDob(m.date_of_birth),
    icon: pickRelationIcon(m.relation, m.gender),
    color: pickRelationColor(m.relation),
    sex: m.gender,
    meds: m.medications || [],
  })), [rawMembers]);

  const member = members.find((m) => m.id === selMember) || members[0];

  const loadAll = async () => {
    // Always fetch public-ish catalog data (packages + shops) — backend requires auth, so if not logged in, fallback to empty.
    try {
      const [p, s] = await Promise.all([
        api.get("/checkups/packages").catch(() => ({ data: [] })),
        api.get("/shops/nearby").catch(() => ({ data: [] })),
      ]);
      setPackages(p.data || []);
      setShops(s.data || []);
    } catch (e) { /* ignore */ }

    if (!user) { setCredits(0); setCash(0); setTxns([]); setRawMembers([]); setBookingsCount(0); return; }
    try {
      // Use allSettled so a single failing endpoint (e.g. transient 5xx) doesn't
      // strand the rest. Each call falls back to a safe default — the Profile
      // tab stays usable even when one API hiccups.
      const [wR, tR, fR, bR] = await Promise.allSettled([
        api.get("/rewards/wallet"),
        api.get("/rewards/transactions"),
        api.get("/family"),
        api.get("/bookings"),
      ]);
      const w = wR.status === "fulfilled" ? wR.value : { data: { points: 0 } };
      const t = tR.status === "fulfilled" ? tR.value : { data: [] };
      const f = fR.status === "fulfilled" ? fR.value : { data: [] };
      const b = bR.status === "fulfilled" ? bR.value : { data: [] };
      setCredits(w.data.points || 0);
      setTxns(t.data || []);
      setRawMembers(f.data || []);
      setBookingsCount((b.data || []).length);
      if (!selMember && (f.data || []).length) setSelMember(f.data[0].id);

      // Hydrate chat history per member — so the Deep Review banner threshold
      // (≥3 user turns) survives a page reload and doesn't reset to 0.
      try {
        const sessionsResp = await api.get("/chat/sessions");
        const sessionsByMember = {};
        for (const s of sessionsResp.data || []) {
          if (!s.family_member_id) continue;
          if (!sessionsByMember[s.family_member_id]) sessionsByMember[s.family_member_id] = s;
        }
        const nextSidMap = {};
        const nextChatsMap = {};
        await Promise.all(Object.entries(sessionsByMember).map(async ([fid, sess]) => {
          nextSidMap[fid] = sess.id;
          try {
            const h = await api.get(`/chat/history/${sess.id}`);
            nextChatsMap[fid] = (h.data || []).map((m, i) => ({
              id: `h-${sess.id}-${i}`,
              role: m.role === "user" ? "user" : "ai",
              content: m.content || "",
              urgent: false,
            }));
          } catch { /* ignore */ }
        }));
        setChatSessionIds((ids) => ({ ...nextSidMap, ...ids }));
        setChats((c) => ({ ...nextChatsMap, ...c }));
      } catch { /* ignore */ }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const memberChats = member ? (chats[member.id] || []) : [];

  const sendChat = async (overrideText) => {
    const msg = (overrideText !== undefined ? overrideText : chatInput).trim();
    if (!msg || chatLoading || !member) return;
    if (!requireAuth("Sign in to chat with the AI companion about your family.")) return;
    if (overrideText === undefined) setChatInput("");
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: msg };
    setChats((c) => ({ ...c, [member.id]: [...(c[member.id] || []), userMsg] }));
    setChatLoading(true);
    try {
      const res = await api.post("/chat/message", {
        family_member_id: member.id,
        session_id: chatSessionIds[member.id] || null,
        message: msg,
      });
      setChatSessionIds((ids) => ({ ...ids, [member.id]: res.data.session_id }));
      setChats((c) => ({
        ...c,
        [member.id]: [...(c[member.id] || []), { id: `a-${Date.now()}`, role: "ai", content: res.data.reply, urgent: res.data.urgent }],
      }));
    } catch (e) {
      setChats((c) => ({
        ...c,
        [member.id]: [...(c[member.id] || []), { id: `e-${Date.now()}`, role: "ai", content: "Having trouble right now. Try again in a moment." }],
      }));
    } finally { setChatLoading(false); }
  };

  const toggleSymptom = async (sym) => {
    if (!member) return;
    if (!requireAuth("Sign in to track symptoms privately for your family.")) return;
    try {
      const res = await api.post(`/family/${member.id}/symptoms`, { symptom: sym, severity: "mild", notes: "" });
      setSymptoms((prev) => ({
        ...prev,
        [member.id]: [res.data, ...(prev[member.id] || [])],
      }));
      showToast(`Logged: ${sym} for ${member.name} ✓`);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Could not log symptom");
    }
  };

  const deleteSymptom = async (logId) => {
    if (!member) return;
    try {
      await api.delete(`/family/${member.id}/symptoms/${logId}`);
      setSymptoms((prev) => ({
        ...prev,
        [member.id]: (prev[member.id] || []).filter((s) => s.id !== logId),
      }));
    } catch {
      showToast("Could not remove entry");
    }
  };

  // When the selected member changes, load their persisted symptom history once
  useEffect(() => {
    if (!user || !member) return;
    if (symptoms[member.id]) return; // already fetched
    api.get(`/family/${member.id}/symptoms`)
      .then((r) => setSymptoms((prev) => ({ ...prev, [member.id]: r.data || [] })))
      .catch(() => { /* ignore — may be guest or network blip */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id, user?.id]);

  // Load + poll the user's active Q-commerce orders so the floating "track" pill stays fresh
  const loadActiveOrders = async () => {
    if (!user) { setActiveOrders([]); return; }
    try {
      const { data } = await api.get("/orders/mine");
      const active = (data || []).filter((o) => ["placed", "confirming_stock", "picked_up", "in_transit", "shipped"].includes(o.status));
      setActiveOrders(active);
    } catch { /* network blip — ignore */ }
  };
  useEffect(() => {
    loadActiveOrders();
    if (!user) return;
    const t = setInterval(loadActiveOrders, 20000); // refresh every 20s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleBuyNow = (product) => {
    if (!requireAuth("Sign in to order with Cash on Delivery.")) {
      // Remember intent so we can auto-resume checkout right after sign-in.
      try { sessionStorage.setItem("pending_buy_product_id", product.id); } catch (_) {}
      return;
    }
    setBuyNowProduct(product);
  };

  // Auto-resume Buy Now if a guest tapped "Buy Now" from a shared shop catalogue
  // (PublicShop) or got bounced to sign-in. We pull the pending product id from
  // sessionStorage, fetch its current snapshot and open the COD modal.
  useEffect(() => {
    if (!user) return;
    let pid;
    try { pid = sessionStorage.getItem("pending_buy_product_id"); } catch (_) { pid = null; }
    if (!pid) return;
    try { sessionStorage.removeItem("pending_buy_product_id"); } catch (_) {}
    api.get("/mapbazzar/products")
      .then((r) => {
        const p = (r.data || []).find((x) => x.id === pid);
        if (p) setBuyNowProduct(p);
      })
      .catch(() => { /* ignore — product may have been removed */ });
  }, [user?.id]);
  const handleOrderPlaced = (order) => {
    setBuyNowProduct(null);
    setTrackerOrderId(order.id);
    loadActiveOrders();
    // Award reward points (1 pt per ₹1) and pop a satisfying funded-% toast
    // so the user immediately sees the Essentials Check bar move on the map.
    const awarded = Number(order?.points_awarded || 0);
    const newBalance = Number(order?.wallet_points || 0);
    if (awarded > 0) {
      setCredits(newBalance);
      const pct = Math.min(100, Math.round((newBalance / ESSENTIALS_GOAL) * 100));
      const funded = newBalance >= ESSENTIALS_GOAL;
      showToast(
        funded
          ? `+${awarded} HealthCoins! 🌱 You've unlocked your free Essentials Check — Book Now!`
          : `+${awarded} HealthCoins added! You're ${pct}% to your free check.`
      );
    }
  };

  // Cart (multi-item COD) order placed → award HealthCoins + refresh active orders.
  const handleCartPlaced = (order) => {
    loadActiveOrders();
    const awarded = Number(order?.points_awarded || 0);
    const newBalance = Number(order?.wallet_points || 0);
    if (awarded > 0) {
      setCredits(newBalance);
      const pct = Math.min(100, Math.round((newBalance / ESSENTIALS_GOAL) * 100));
      const funded = newBalance >= ESSENTIALS_GOAL;
      showToast(
        funded
          ? `+${awarded} HealthCoins! 🌱 Free Essentials Check unlocked — Book Now!`
          : `+${awarded} HealthCoins added! You're ${pct}% to your free check.`
      );
    }
  };

  // Open the cart automatically when a shopper taps "Checkout" on a public shop page.
  useEffect(() => {
    let f;
    try { f = sessionStorage.getItem("open_cart"); } catch (_) { f = null; }
    if (f) {
      try { sessionStorage.removeItem("open_cart"); } catch (_) { /* ignore */ }
      setCartOpen(true);
    }
  }, []);

  const handleEssentialsBooked = async () => {
    setEssentialsBookOpen(false);
    showToast("✓ Booked. Kirit will arrive at your slot — check WhatsApp for confirmation.");
    // Refresh wallet so the bar resets toward the next check
    try {
      const w = await api.get("/rewards/wallet");
      setCredits(w.data.points || 0);
    } catch (_) { /* ignore */ }
    setBookingsCount((c) => c + 1);
  };

  const openEssentialsBook = () => {
    if (!requireAuth("Sign in to book your free Essentials Check.")) return;
    if (credits < ESSENTIALS_GOAL) {
      showToast(`Need ${ESSENTIALS_GOAL - credits} more HealthCoins — shop on Mapbazar to earn them.`);
      return;
    }
    setEssentialsBookOpen(true);
  };

  const giveLocalReward = (source) => {
    const r = REWARD_POOL[Math.floor(Math.random() * REWARD_POOL.length)];
    if (r.type === "cash") {
      setCash((x) => x + 50);
      setCredits((c) => c + r.credits);
      showToast(`Surprise! ₹50 cashback + ${r.credits} credits 💸`);
    } else if (r.type === "bonus") {
      setCredits((c) => c + r.credits);
      showToast(`Bonus! +${r.credits} credits 🎉`);
    } else {
      setCredits((c) => c + r.credits);
      showToast(`+${r.credits} credits from ${source} 🌱`);
    }
  };

  const openPartner = (partner) => {
    if (!requireAuth("Sign in to shop through our partners and earn rewards.")) return;
    // Open the real affiliate URL directly in a new tab.
    const url = buildAffiliateUrl(partner, searchQuery || "");
    try { window.open(url, "_blank", "noopener,noreferrer"); } catch { /* popup blocked */ }
    showToast(`🛒 Opened ${partner.name} — your purchase funds family check-ins 🙏`);
  };
  const completePurchase = async (partner, query) => {
    setBrowserModal(null);
    if (!user) return;
    try {
      await api.post("/invoices/upload", {
        merchant: partner.name, amount: 1500, date: new Date().toISOString().slice(0, 10), category: "other",
      });
      showToast(`Purchase logged — awaiting verification 🌱`);
      loadAll();
    } catch { giveLocalReward(partner.name); }
  };

  const earnFromLocal = (shopName) => {
    if (!requireAuth("Sign in to earn rewards when you visit local shops.")) return;
    giveLocalReward(shopName);
  };

  const submitReceipt = async ({ merchant, amount, date }) => {
    if (!requireAuth("Sign in to upload receipts and earn rewards.")) return;
    try {
      await api.post("/invoices/upload", { merchant, amount, date, category: "other" });
      showToast("Receipt submitted — pending verification 📸");
      setReceiptModal(false);
      loadAll();
    } catch (e) {
      showToast("Couldn't upload receipt. Try again.");
    }
  };

  const bookPackage = async (pkg) => {
    if (!requireAuth("Sign in to book a preventive check-in.")) return;
    if (!member) { showToast("Add a family member first"); return; }
    if (credits < pkg.points_cost) { showToast(`Need ${pkg.points_cost - credits} more credits`); return; }
    try {
      if (pkg.lifeline_id) {
        await api.post("/bookings/lifeline", {
          screening_id: pkg.lifeline_id, title: pkg.name, description: pkg.description,
          points_cost: pkg.points_cost, family_member_id: member.id,
          preferred_date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
        });
      } else {
        await api.post("/bookings", {
          package_id: pkg.id, family_member_id: member.id,
          preferred_date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
          points_to_apply: pkg.points_cost,
        });
      }
      setBookingModal(null);
      showToast(`✅ Booked ${pkg.name}. Lab calls within 24h.`);
      loadAll();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Booking failed");
    }
  };

  const setReminder = (pkg, dateStr) => {
    if (!requireAuth("Sign in to set preventive check-in reminders.")) return;
    if (!dateStr) { showToast("Pick a reminder date"); return; }
    try {
      const key = "ibloom_reminders";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      const memberName = member?.name || "Family";
      const entry = {
        id: `rem_${Date.now()}`,
        package_id: pkg.id,
        title: pkg.name,
        description: pkg.description || "",
        member_id: member?.id || null,
        member_name: memberName,
        remind_on: dateStr,
        created_at: new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify([entry, ...existing].slice(0, 50)));

      // Build a tiny .ics calendar file the user can add to any calendar app.
      const dt = dateStr.replace(/-/g, "");
      const ics = [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Mapbazar//Reminder//EN",
        "BEGIN:VEVENT",
        `UID:${entry.id}@ibloom.ai`,
        `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "")}`,
        `DTSTART;VALUE=DATE:${dt}`,
        `DTEND;VALUE=DATE:${dt}`,
        `SUMMARY:Check-in reminder · ${pkg.name} for ${memberName}`,
        `DESCRIPTION:${(pkg.description || "").replace(/\n/g, " ")}`,
        "END:VEVENT", "END:VCALENDAR",
      ].join("\r\n");
      const blob = new Blob([ics], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pkg.name.replace(/\s+/g, "_")}_reminder.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setBookingModal(null);
      const nice = new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      showToast(`🔔 Reminder set for ${nice} · calendar file saved`);
    } catch (e) {
      showToast("Couldn't set reminder");
    }
  };

  const addMember = async (d) => {
    if (!requireAuth("Sign in to add family members.")) return;
    const dob = new Date();
    dob.setFullYear(dob.getFullYear() - parseInt(d.age, 10));
    try {
      const res = await api.post("/family", {
        name: d.name, relation: d.relation, gender: d.sex,
        date_of_birth: dob.toISOString().slice(0, 10),
        conditions: d.conditions ? d.conditions.split(",").map((x) => x.trim()).filter(Boolean) : [],
        medications: [], notes: "",
        height_cm: d.height_cm ? parseFloat(d.height_cm) : null,
        weight_kg: d.weight_kg ? parseFloat(d.weight_kg) : null,
        primary_focus: d.primary_focus || null,
      });
      setAddMemberModal(false);
      showToast(`✅ ${d.name} added`);
      await loadAll();
      setSelMember(res.data.id);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Couldn't add member");
    }
  };

  const tryOpenAddMember = (preset) => {
    if (!requireAuth("Sign in to add family members — they'll get personalised preventive reminders.")) return;
    setAddMemberModal(preset && typeof preset === "object" ? preset : true);
  };

  const saveHealthProfile = async (patch) => {
    if (!profileEditorFor) return;
    try {
      await api.patch(`/family/${profileEditorFor.id}/profile`, patch);
      showToast("✅ Health profile updated");
      setProfileEditorFor(null);
      await loadAll();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Couldn't save profile");
    }
  };

  const tryOpenReceipt = () => {
    if (!requireAuth("Sign in to upload receipts and start earning rewards.")) return;
    setReceiptModal(true);
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const SignInModal = ({ reason, onClose }) => (
    <div style={{ ...S.modalOverlay, zIndex: 1200 }} data-testid="signin-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 32, maxWidth: 420, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
        <div style={{ ...S.fraunces, fontSize: 24, fontWeight: 700, marginBottom: 10, color: "#1A1A1A" }}>Sign in to continue</div>
        <div style={{ fontSize: 13.5, color: "#555", lineHeight: 1.6, marginBottom: 20 }}>{reason}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", fontWeight: 600, cursor: "pointer" }} data-testid="signin-later-btn">Maybe later</button>
          <button onClick={() => navigate("/login")} style={{ flex: 2, padding: 13, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }} data-testid="signin-go-btn">Sign in · 10 sec</button>
        </div>
      </div>
    </div>
  );

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div style={S.app}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,500;0,600;0,700;1,400;1,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
      `}</style>

      {toast && <div style={{ ...S.toast, pointerEvents: "none" }} data-testid="toast">{toast}</div>}

      {whyModal && <WhyModal onClose={() => setWhyModal(false)} />}
      {storyModal && <StoryModal onClose={() => setStoryModal(false)} />}
      {tourOpen && <AppTourOverlay onClose={() => setTourOpen(false)} />}
      {bookingModal && <BookingModal pkg={bookingModal} credits={credits} member={member} onSetReminder={setReminder} onClose={() => setBookingModal(null)} />}
      {receiptModal && <ReceiptModal onClose={() => setReceiptModal(false)} onSubmit={submitReceipt} />}
      {addMemberModal && <AddMemberForm onSubmit={addMember} onCancel={() => setAddMemberModal(false)} preset={typeof addMemberModal === "object" ? addMemberModal : null} />}
      {profileEditorFor && <HealthProfileModal member={profileEditorFor} onSave={saveHealthProfile} onClose={() => setProfileEditorFor(null)} />}
      {browserModal && <BrowserModal browser={browserModal} onComplete={completePurchase} onClose={() => setBrowserModal(null)} />}
      {signInModal && <SignInModal reason={signInModal} onClose={() => setSignInModal(null)} />}

      {buyNowProduct && (
        <BuyNowModal
          product={buyNowProduct}
          user={user}
          onClose={() => setBuyNowProduct(null)}
          onPlaced={handleOrderPlaced}
          showToast={showToast}
        />
      )}
      {trackerOrderId && (
        <OrderTrackerModal
          orderId={trackerOrderId}
          onClose={() => { setTrackerOrderId(null); loadActiveOrders(); }}
          showToast={showToast}
        />
      )}
      {deliveryOpen && (
        <DeliveryPartner onClose={() => setDeliveryOpen(false)} showToast={showToast} />
      )}
      {cartOpen && (
        <CartModal
          user={user}
          onClose={() => setCartOpen(false)}
          onPlaced={handleCartPlaced}
          onTrack={(oid) => { setCartOpen(false); setTrackerOrderId(oid); }}
          showToast={showToast}
        />
      )}
      {essentialsBookOpen && (
        <EssentialsCheckBookModal
          user={user}
          members={members}
          onClose={() => setEssentialsBookOpen(false)}
          onBooked={handleEssentialsBooked}
          showToast={showToast}
        />
      )}

      <div style={S.header} data-testid="app-header">
        <div style={{ ...S.headerInner, ...(isMobile ? { padding: "8px 14px" } : {}) }}>
          <button onClick={() => setTab("shop")} style={S.logoRow} data-testid="header-logo-btn">
            <img src="/icon-192.png" alt="Mapbazar" width={isMobile ? 28 : 32} height={isMobile ? 28 : 32} style={{ borderRadius: 9, display: "block" }} data-testid="header-logo-mark" />
            <div style={{ textAlign: "left", minWidth: 0 }}>
              <div style={{ ...S.fraunces, ...S.logoName, ...(isMobile ? { fontSize: 17 } : {}) }}>
                {tab === "health" ? (<>Rewards <span style={{ color: "#C97B3C", fontWeight: 500 }}>&amp;</span> HealthCoin</>)
                  : tab === "credits" ? (<>Rewards</>)
                  : tab === "profile" ? (<>Mapbazar</>)
                  : (<>Mapbazar</>)}
              </div>
              <div style={{ ...S.logoSub, ...((tab === "shop" || tab === "home") ? { letterSpacing: 0.1, textTransform: "none", fontSize: isMobile ? 10 : 10.5 } : {}) }} data-testid="header-subtitle">
                {(tab === "shop" || tab === "home") ? "Shop Local, Stay Healthy" : tab === "credits" ? "Your wellness wallet" : tab === "health" ? "Fund your health insurance with HealthCoins" : "Your Mapbazar account"}
              </div>
            </div>
          </button>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginRight: 40 }}>
            <button data-testid="header-cart-btn" onClick={() => setCartOpen(true)} aria-label="Cart"
              style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", border: "1px solid #EAE8DC", background: "white", color: "#0F3D2E", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              🛒
              {cart.count > 0 && (
                <span data-testid="header-cart-count" style={{ position: "absolute", top: -4, right: -4, background: "#C97B3C", color: "white", fontSize: 9, fontWeight: 800, minWidth: 16, height: 16, borderRadius: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>
                  {cart.count}
                </span>
              )}
            </button>
            {cash > 0 && (
              <div style={S.cashPill} data-testid="cash-pill">
                <span style={{ fontSize: 12 }}>💸</span>
                <span style={S.cashNum}>₹{cash}</span>
              </div>
            )}
            <div style={S.creditPill} onClick={() => { setTab("health"); setHealthView("rewards"); }} data-testid="credit-pill">
              <span style={{ fontSize: 14 }}>🌱</span>
              <span style={S.creditNum}>{credits.toLocaleString()}</span>
            </div>
            <button onClick={() => setTab("profile")} style={S.avatarBtn} data-testid="header-avatar-btn">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </button>
          </div>
        </div>
      </div>

      <div style={S.main}>
        <Suspense fallback={<div data-testid="tab-loading" style={{ padding: "70px 16px", textAlign: "center", color: "#8A94A6", fontSize: 13 }}>Loading…</div>}>
        {tab === "home" && <MapBazzarTab requireAuth={requireAuth} showToast={showToast} onPickupBooked={loadAll} openPartner={openPartner} user={user} onBuyNow={handleBuyNow} credits={credits} onBookEssentialsCheck={openEssentialsBook} onOpenRewards={() => setTab("health")} />}
        {tab === "shop" && <MapBazzarTab requireAuth={requireAuth} showToast={showToast} onPickupBooked={loadAll} openPartner={openPartner} user={user} onBuyNow={handleBuyNow} credits={credits} onBookEssentialsCheck={openEssentialsBook} onOpenRewards={() => setTab("health")} />}
        {tab === "health" && member && (
          <HealthTab
            members={members} selMember={member.id} setSelMember={setSelMember} setAddMemberModal={tryOpenAddMember}
            healthView={healthView} setHealthView={setHealthView}
            packages={packages} credits={credits} setBookingModal={(p) => { if (requireAuth("Sign in to book a preventive check-in.")) setBookingModal(p); }}
            memberChats={memberChats} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} chatLoading={chatLoading}
            chatSessionId={chatSessionIds[member.id] || null}
            symptoms={symptoms} toggleSymptom={toggleSymptom} deleteSymptom={deleteSymptom} member={member}
            requireAuth={requireAuth} showToast={showToast} onBooked={loadAll}
            openHealthProfile={() => setProfileEditorFor(member)}
            authUser={user}
            cash={cash} txns={txns} setTab={setTab}
            setWhyModal={setWhyModal} setStoryModal={setStoryModal}
          />
        )}
        {tab === "health" && !member && (
          <HealthTab members={[]} setAddMemberModal={tryOpenAddMember} healthView={healthView} setHealthView={setHealthView} packages={packages} credits={credits} setBookingModal={(p) => { if (requireAuth("Sign in to book a preventive check-in.")) setBookingModal(p); }} memberChats={[]} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} chatLoading={chatLoading} symptoms={symptoms} toggleSymptom={toggleSymptom} deleteSymptom={deleteSymptom} member={null} requireAuth={requireAuth} showToast={showToast} onBooked={loadAll} />
        )}
        {tab === "profile" && user && (
          <ProfileTab credits={credits} cash={cash} members={members} user={user} bookingsCount={bookingsCount} checkinsCount={bookingsCount} onLogout={handleLogout} setStoryModal={setStoryModal} setWhyModal={setWhyModal} onShowTour={() => setTourOpen(true)} showToast={showToast} onTrackOrder={(oid) => setTrackerOrderId(oid)} onOpenDelivery={() => setDeliveryOpen(true)} />
        )}
        {tab === "profile" && !user && authLoading && (
          <div data-testid="profile-loading" style={{ padding: "60px 16px", textAlign: "center" }}>
            <div style={{ width: 36, height: 36, margin: "0 auto 14px", borderRadius: "50%", border: "3px solid #E8F0EC", borderTopColor: "#0F3D2E", animation: "ibloomSpin 0.8s linear infinite" }} />
            <div style={{ fontSize: 13, color: "#666" }}>Loading your profile…</div>
            <style>{`@keyframes ibloomSpin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {tab === "profile" && !user && !authLoading && (
          <div data-testid="profile-guest" style={{ padding: "40px 10px", textAlign: "center" }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>👋</div>
            <div style={{ ...S.fraunces, fontSize: 24, fontWeight: 700, marginBottom: 8, color: "#1A1A1A" }}>Sign in to Mapbazar</div>
            <div style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, maxWidth: 360, margin: "0 auto 20px" }}>
              Track your family's preventive care, earn credits from everyday shopping, and redeem them for routine check-ins.
            </div>
            <button onClick={() => navigate("/login")} data-testid="profile-signin-btn" style={{ padding: "12px 28px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Sign in · 10 sec</button>
            <div style={{ marginTop: 24 }}>
              <button onClick={() => setStoryModal(true)} style={{ background: "none", border: "none", color: "#999", fontSize: 12, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Why Mapbazar exists →</button>
            </div>
          </div>
        )}
        </Suspense>
      </div>

      {/* Floating "Track Order" pill — visible whenever the user has an active Q-commerce order */}
      {activeOrders.length > 0 && !buyNowProduct && !trackerOrderId && (
        <button
          data-testid="track-order-fab"
          onClick={() => setTrackerOrderId(activeOrders[0].id)}
          style={{
            position: "fixed", left: "50%", bottom: 86, transform: "translateX(-50%)",
            zIndex: 900, padding: "12px 20px", borderRadius: 100, border: "none",
            background: "#0F3D2E", color: "white", fontWeight: 800, fontSize: 13,
            display: "flex", alignItems: "center", gap: 8,
            boxShadow: "0 10px 26px rgba(15,61,46,0.4)", cursor: "pointer",
            animation: "pulseTrack 1.8s ease-in-out infinite",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FFD54A", boxShadow: "0 0 0 4px rgba(255,213,74,0.35)" }} />
          🛵 Track your order ({activeOrders.length})
        </button>
      )}
      <style>{`@keyframes pulseTrack { 0%,100% { transform: translateX(-50%) scale(1); } 50% { transform: translateX(-50%) scale(1.04); } }`}</style>

      <div style={S.bottomNav} data-testid="bottom-nav">
        {[
          { id: "shop", label: "Mapbazar", icon: "🛒" },
          { id: "health", label: "Rewards", icon: "🛡️" },
          { id: "profile", label: "Profile", icon: "👤" },
        ].map((t) => (
          <button key={t.id} data-testid={`bottom-nav-${t.id}`} onClick={() => setTab(t.id)} style={{
            ...S.navBtn,
            color: tab === t.id ? "#0F3D2E" : "#888",
            background: tab === t.id ? "rgba(15,61,46,0.08)" : "transparent",
          }}>
            <span style={{ fontSize: 20 }}>{t.icon}</span>
            <span style={{ fontSize: 9.5, fontWeight: tab === t.id ? 700 : 500, marginTop: 2 }}>{t.label}</span>
          </button>
        ))}
      </div>

      <PwaInstallBanner />
    </div>
  );
}